import { useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import {
  ScanBarcode,
  Receipt,
  ReceiptText,
  Package,
  Users,
  Wallet,
  ChartColumn,
  LayoutDashboard,
  Settings,
  LogOut,
  Menu,
  X,
  BookOpen,
} from 'lucide-react'
import { OctaPosMark } from '@/components/OctaPosMark'
import { Button } from '@/components/ui/Button'
import { ThemeToggle } from '@/components/ThemeToggle'
import { LanguageSwitch } from '@/components/LanguageSwitch'
import { msg, t } from '@/i18n'
import { useSession } from '@/stores/session'
import { Permission, type PermissionValue } from '@/lib/permissions'
import { LicenseLine, LicenseWarning } from '@/components/LicenseNotice'
import { cn } from '@/lib/cn'

interface NavItem {
  to: string
  label: string
  icon: typeof ScanBarcode
  /** Hidden unless the user holds this right. The server enforces it regardless. */
  permission?: PermissionValue
}

const navItems: NavItem[] = [
  { to: '/', label: msg('Checkout'), icon: ScanBarcode },
  { to: '/dashboard', label: msg('Today'), icon: LayoutDashboard, permission: Permission.ViewDashboard },
  { to: '/sales', label: msg('Sales'), icon: Receipt },
  { to: '/inventory', label: msg('Inventory'), icon: Package, permission: Permission.ManageStock },
  { to: '/customers', label: msg('Customers'), icon: Users },
  { to: '/expenses', label: msg('Expenses'), icon: ReceiptText, permission: Permission.RecordExpense },
  { to: '/cash', label: msg('Cash'), icon: Wallet, permission: Permission.ManageSafe },
  { to: '/reports', label: msg('Reports'), icon: ChartColumn, permission: Permission.ViewReports },
  { to: '/settings', label: msg('Settings'), icon: Settings, permission: Permission.ManageSettings },
  { to: '/help', label: msg('Guide'), icon: BookOpen },
]

export function AppShell() {
  const navigate = useNavigate()
  const session = useSession((state) => state.session)
  const logout = useSession((state) => state.logout)
  const canDo = useSession((state) => state.can)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  // The branch this user works at, so the header can name it under the store. Shared cache
  // with the screens that already list branches, so this costs no extra request in practice.
  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => apiClient.get<{ id: number; name: string }[]>('/api/branches'),
    enabled: session?.branchId != null,
  })

  const branchName = branches.data?.find((branch) => branch.id === session?.branchId)?.name ?? null

  const visibleItems = navItems.filter((item) => !item.permission || canDo(item.permission))

  async function handleLogout() {
    await logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="surface-raised sticky top-0 z-30 border-b">
        <div className="flex h-14 items-center gap-3 px-3 sm:px-4">
          <button
            type="button"
            onClick={() => setIsMenuOpen((open) => !open)}
            className="touch-target -ml-1 flex items-center justify-center rounded-lg lg:hidden"
            aria-label={isMenuOpen ? t('Close menu') : t('Open menu')}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>

          {/* Decorative: the store name sits right beside it, so it needs no second label. */}
          <OctaPosMark title={null} className="h-7 w-7 shrink-0 text-[var(--accent)]" />

          {/*
            The store name always leads and the branch is subordinate to it. A branch is never
            shown on its own anywhere in the app — that is what stops it reading as a separate
            shop. An Admin is not tied to a branch, so they see the store alone.
          */}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold leading-tight">
              {session?.storeName ?? 'OctaPOS'}
            </p>
            {branchName && (
              <p className="truncate text-xs leading-tight text-[var(--text-secondary)]">
                {branchName}
              </p>
            )}
            <p className="truncate text-xs leading-tight text-[var(--text-secondary)]">
              {session?.displayName} · {session?.role}
            </p>
          </div>

          <LanguageSwitch />

          <ThemeToggle />

          <Button variant="ghost" size="icon" onClick={handleLogout} aria-label={t('Sign out')}>
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Desktop rail */}
        <nav className="surface-raised hidden w-56 shrink-0 flex-col border-e p-3 lg:flex">
          <NavList items={visibleItems} />
          {/* Under the last nav item, where it is visible without being in the way. */}
          <LicenseLine />
        </nav>

        {/* Mobile drawer */}
        {isMenuOpen && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <button
              type="button"
              className="absolute inset-0 bg-black/40"
              aria-label={t('Close menu')}
              onClick={() => setIsMenuOpen(false)}
            />
            <nav className="surface-raised absolute inset-y-0 start-0 flex w-64 flex-col border-e p-3 pt-16">
              <NavList items={visibleItems} onNavigate={() => setIsMenuOpen(false)} />
              <LicenseLine />
            </nav>
          </div>
        )}

        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>

      <LicenseWarning />
    </div>
  )
}

function NavList({ items, onNavigate }: { items: NavItem[]; onNavigate?: () => void }) {
  return (
    <ul className="flex flex-col gap-1">
      {items.map(({ to, label, icon: Icon }) => (
        <li key={to}>
          <NavLink
            to={to}
            end={to === '/'}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'touch-target flex items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-[var(--accent)] text-[var(--accent-contrast)]'
                  : 'text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]',
              )
            }
          >
            <Icon className="h-5 w-5 shrink-0" />
            {t(label)}
          </NavLink>
        </li>
      ))}
    </ul>
  )
}
